import {
  Directive,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnInit,
  Renderer2,
  SecurityContext,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ContextService } from '@services/context';
import { IconsService } from '@services/icons';
import { Color } from '@shared/enums';
import { AvailableIcons } from '@shared/enums/available-icons';
import { filter, take } from 'rxjs';

@Directive({
  selector: '[iconName]',
  standalone: true,
})
export class IconsDirective implements OnInit, OnChanges {
  readonly iconName = input.required<keyof typeof AvailableIcons>();
  readonly color = input<keyof typeof Color>('BLACK');
  readonly size = input('24px');
  readonly refresh = input(false);
  private el = inject<ElementRef<Element>>(ElementRef);
  private renderer2 = inject(Renderer2);
  private domSanitizer = inject(DomSanitizer);
  private iconsService = inject(IconsService);
  private contextService = inject(ContextService);

  ngOnChanges(changes: SimpleChanges): void {
    if (this.refresh() && changes['size'] && !changes['size'].firstChange) {
      const nativeEl = this.el.nativeElement as HTMLElement;
      nativeEl.setAttribute('width', changes['size'].currentValue as string);
      nativeEl.setAttribute('height', changes['size'].currentValue as string);
      const svg = nativeEl.getElementsByTagName('svg')[0];
      if (svg) {
        const size = this.size();
        svg.setAttribute('width', size);
        svg.setAttribute('height', size);
      }
    }
  }

  ngOnInit(): void {
    if (this.contextService.isServer()) return;
    this.iconsService
      .getIcons(this.iconName())
      .pipe(
        take(1),
        filter((icon) => !!icon)
      )
      .subscribe((icon) => {
        this.renderer2.setProperty(
          this.el.nativeElement,
          'innerHTML',
          this.domSanitizer.sanitize(SecurityContext.HTML, this.domSanitizer.bypassSecurityTrustHtml(icon))
        );
        const size = this.size();
        this.el.nativeElement.setAttribute('width', size);
        this.el.nativeElement.setAttribute('height', size);
        this.el.nativeElement.classList.add('icon');
        const svg = this.el.nativeElement.getElementsByTagName('svg')[0];
        if (svg) {
          svg.setAttribute('width', size);
          svg.setAttribute('height', size);
          const path = svg.getElementsByTagName('path');
          Array.from(path).map((p) => {
            const color = this.color();
            p.setAttribute('fill', color in Color ? Color[color] : color);
          });
        }
      });
  }
}
